import React, { useEffect, useState } from 'react';
import api from '../../utils/api';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const DAY_MAP = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
  thursday: 4, friday: 5, saturday: 6
};

const getNthWeekday = (year, month, weekday, n) => {
  const first = new Date(year, month, 1);
  const diff = (weekday - first.getDay() + 7) % 7;
  return new Date(year, month, 1 + diff + (n - 1) * 7);
};

const getLastWeekday = (year, month, weekday) => {
  const last = new Date(year, month + 1, 0);
  const diff = (last.getDay() - weekday + 7) % 7;
  return new Date(year, month, last.getDate() - diff);
};

const getUSHolidays = (year) => [
  { date: new Date(year, 0, 1), name: "New Year's Day" },
  { date: getNthWeekday(year, 0, 1, 3), name: 'MLK Day' },
  { date: getNthWeekday(year, 1, 1, 3), name: "Presidents' Day" },
  { date: getLastWeekday(year, 4, 1), name: 'Memorial Day' },
  { date: new Date(year, 5, 19), name: 'Juneteenth' },
  { date: new Date(year, 6, 4), name: 'Independence Day' },
  { date: getNthWeekday(year, 8, 1, 1), name: 'Labor Day' },
  { date: getNthWeekday(year, 9, 1, 2), name: 'Columbus Day' },
  { date: new Date(year, 10, 11), name: "Veterans Day" },
  { date: getNthWeekday(year, 10, 4, 4), name: 'Thanksgiving' },
  { date: new Date(year, 11, 25), name: 'Christmas Day' },
];

const dateKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const StudentCalendar = () => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [enrollments, setEnrollments] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await api.get('/enroll/my');
        const enrs = (data.enrollments || []).filter(e => e.status === 'active' && e.course);
        setEnrollments(enrs);

        // Fetch attendance for each enrolled course
        const attResults = await Promise.allSettled(
          enrs.map(e => api.get(`/attendance/my/${e.course._id}`))
        );

        const aMap = {};
        enrs.forEach((e, i) => {
          if (attResults[i].status === 'fulfilled') {
            const records = attResults[i].value.data.attendance || [];
            records.forEach(r => {
              const key = dateKey(new Date(r.date));
              if (!aMap[key]) aMap[key] = [];
              aMap[key].push({ courseName: e.course.name, status: r.status });
            });
          }
        });
        setAttendanceMap(aMap);
      } catch (err) {
        console.error('Failed to load calendar data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const prevMonth = () =>
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const nextMonth = () =>
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  const goToday = () => {
    const now = new Date();
    setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  const buildEvents = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const events = {};

    const addEvent = (date, event) => {
      const key = dateKey(date);
      if (!events[key]) events[key] = [];
      events[key].push(event);
    };

    // US Holidays
    const years = [year];
    if (month === 0) years.push(year - 1);
    if (month === 11) years.push(year + 1);
    years.forEach(y => {
      getUSHolidays(y).forEach(h =>
        addEvent(h.date, { type: 'us_holiday', name: h.name })
      );
    });

    // Studio holidays (from any enrollment's studio)
    const seenHolidays = new Set();
    enrollments.forEach(e => {
      (e.studio?.holidays || []).forEach(h => {
        const key = h.name + h.date;
        if (!seenHolidays.has(key)) {
          seenHolidays.add(key);
          addEvent(new Date(h.date), { type: 'studio_holiday', name: h.name });
        }
      });
    });

    // Course sessions (only enrolled courses)
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    const gridStart = new Date(year, month, 1 - firstDay);
    const gridEnd = new Date(year, month, daysInMonth + (42 - daysInMonth - firstDay));

    enrollments.forEach(e => {
      const course = e.course;
      if (course.type === 'long_course' && course.schedule?.days?.length > 0) {
        const courseDays = course.schedule.days.map(d => DAY_MAP[d]);
        const d = new Date(gridStart);
        while (d <= gridEnd) {
          if (courseDays.includes(d.getDay())) {
            addEvent(new Date(d), {
              type: 'class',
              name: course.name,
              time: course.schedule.startTime || ''
            });
          }
          d.setDate(d.getDate() + 1);
        }
      } else if (course.type === 'block' && course.schedule?.blockStartDate) {
        const blockStart = new Date(course.schedule.blockStartDate);
        const blockWeeks = course.schedule.blockWeeks || 8;
        const blockEnd = new Date(blockStart);
        blockEnd.setDate(blockEnd.getDate() + blockWeeks * 7);
        const courseDays = (course.schedule.days || []).map(d => DAY_MAP[d]);
        const d = new Date(Math.max(blockStart.getTime(), gridStart.getTime()));
        const end = new Date(Math.min(blockEnd.getTime(), gridEnd.getTime()));
        while (d <= end) {
          if (courseDays.includes(d.getDay())) {
            addEvent(new Date(d), {
              type: 'block',
              name: course.name,
              time: course.schedule.startTime || ''
            });
          }
          d.setDate(d.getDate() + 1);
        }
      } else if (course.type === 'workshop' && course.schedule?.sessionDate) {
        addEvent(new Date(course.schedule.sessionDate), {
          type: 'workshop',
          name: course.name,
          time: course.schedule.sessionStartTime || ''
        });
      }
    });

    // Payment due dates
    enrollments.forEach(e => {
      if (e.currentPeriodEnd) {
        addEvent(new Date(e.currentPeriodEnd), {
          type: 'payment',
          name: `Payment due: ${e.course.name}`
        });
      }
    });

    return events;
  };

  if (loading) return <div className="text-gray-500">Loading calendar...</div>;

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const events = buildEvents();
  const today = new Date();
  const todayKey = dateKey(today);

  const cells = [];
  const startDate = new Date(year, month, 1 - firstDay);
  for (let i = 0; i < 42; i++) {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    cells.push(d);
  }

  const lastCellMonth = cells[34].getMonth();
  const needsSixRows = lastCellMonth === month || cells[35].getDate() <= daysInMonth;
  const visibleCells = needsSixRows ? cells : cells.slice(0, 35);

  const selectedDayEvents = selectedDay ? (events[selectedDay] || []) : [];
  const selectedDayAttendance = selectedDay ? (attendanceMap[selectedDay] || []) : [];

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Calendar</h1>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Month nav */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <button
            onClick={prevMonth}
            className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors text-gray-600 text-xl"
          >
            &#8249;
          </button>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">
              {MONTH_NAMES[month]} {year}
            </h2>
            <button
              onClick={goToday}
              className="text-xs text-brand-500 hover:text-brand-600 font-medium px-2 py-1 rounded hover:bg-brand-50 transition-colors"
            >
              Today
            </button>
          </div>
          <button
            onClick={nextMonth}
            className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors text-gray-600 text-xl"
          >
            &#8250;
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-gray-100 bg-gray-50">
          {DAY_NAMES.map(d => (
            <div key={d} className="px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {d}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7">
          {visibleCells.map((date, i) => {
            const key = dateKey(date);
            const isCurrentMonth = date.getMonth() === month;
            const isToday = key === todayKey;
            const dayEvents = events[key] || [];
            const dayAttendance = attendanceMap[key] || [];
            const isSelected = selectedDay === key;
            const isPast = date < today && !isToday;

            return (
              <div
                key={i}
                onClick={() => setSelectedDay(isSelected ? null : key)}
                className={`min-h-[90px] border-b border-r border-gray-50 p-1.5 cursor-pointer transition-colors ${
                  !isCurrentMonth ? 'bg-gray-50/50' : ''
                } ${isToday ? 'bg-brand-50/30' : ''} ${
                  isSelected ? 'ring-2 ring-inset ring-brand-400' : ''
                } hover:bg-gray-50/80`}
              >
                <div className="flex items-center gap-1">
                  <span className={`text-sm ${
                    isToday ? 'font-bold' : !isCurrentMonth ? 'text-gray-300' : 'text-gray-700 font-medium'
                  }`}>
                    {isToday ? (
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-brand-500 text-white text-xs font-bold">
                        {date.getDate()}
                      </span>
                    ) : (
                      date.getDate()
                    )}
                  </span>
                  {/* Attendance dots */}
                  {isPast && dayAttendance.length > 0 && (
                    <div className="flex gap-0.5 ml-auto">
                      {dayAttendance.map((a, j) => (
                        <span
                          key={j}
                          className={`w-2 h-2 rounded-full ${
                            a.status === 'present' ? 'bg-green-500' :
                            a.status === 'excused' ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          title={`${a.courseName}: ${a.status}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-0.5 mt-0.5">
                  {dayEvents.slice(0, 3).map((event, j) => (
                    <div
                      key={j}
                      className={`text-[10px] leading-tight px-1 py-0.5 rounded truncate ${
                        event.type === 'us_holiday' ? 'bg-red-50 text-red-600'
                          : event.type === 'studio_holiday' ? 'bg-orange-50 text-orange-700 font-medium'
                          : event.type === 'payment' ? 'bg-green-50 text-green-700'
                          : event.type === 'workshop' ? 'bg-purple-50 text-purple-600'
                          : event.type === 'block' ? 'bg-teal-50 text-teal-600'
                          : 'bg-brand-50 text-brand-600'
                      }`}
                      title={event.name}
                    >
                      {event.name}
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-[10px] text-gray-400 px-1">+{dayEvents.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-4 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-brand-100 inline-block" /> My Classes
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-teal-100 inline-block" /> Block Courses
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-purple-100 inline-block" /> Workshops
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-green-100 inline-block" /> Payments
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-red-100 inline-block" /> US Holidays
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-orange-100 inline-block" /> Studio Holidays
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Present
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Absent
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" /> Excused
        </span>
      </div>

      {/* Day detail panel */}
      {selectedDay && (selectedDayEvents.length > 0 || selectedDayAttendance.length > 0) && (
        <div className="mt-4 bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            {new Date(selectedDay + 'T12:00:00').toLocaleDateString('en-US', {
              weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
            })}
          </h3>
          <div className="space-y-2">
            {selectedDayEvents.map((event, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  event.type === 'us_holiday' ? 'bg-red-500'
                    : event.type === 'studio_holiday' ? 'bg-orange-500'
                    : event.type === 'payment' ? 'bg-green-500'
                    : event.type === 'workshop' ? 'bg-purple-500'
                    : event.type === 'block' ? 'bg-teal-500'
                    : 'bg-brand-500'
                }`} />
                <span className="text-sm text-gray-900">{event.name}</span>
                {event.time && (
                  <span className="text-xs text-gray-500">{event.time}</span>
                )}
              </div>
            ))}
            {selectedDayAttendance.map((a, i) => (
              <div key={`att-${i}`} className="flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  a.status === 'present' ? 'bg-green-500' :
                  a.status === 'excused' ? 'bg-yellow-500' :
                  'bg-red-500'
                }`} />
                <span className="text-sm text-gray-900">{a.courseName}</span>
                <span className={`text-xs font-medium ${
                  a.status === 'present' ? 'text-green-600' :
                  a.status === 'excused' ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {a.status.charAt(0).toUpperCase() + a.status.slice(1)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentCalendar;
